import type { TOC as TOCType } from '~/common/types';
import { applyDbDigitizationBundle, resetDigitizationToLegacy } from './digitizationLayers';
import { applyDbDownloadLayersBundle, resetDownloadLayersToLegacy } from './downloadLayers';
import { applyDbSpecialDatasetsBundle, clearSpecialDatasets, type SpecialDatasetConfig } from './specialDatasets';
import { applyDbTocBundle, resetActiveTocToLegacy } from './TOCLayers';

/** The DB-derived layer config the server delivers at bootstrap (`OrganizationDetail.layerConfig`). */
interface DbLayerConfig {
  toc?: TOCType;
  digitization?: { digitizationLayerIds: string[]; digitizationTOC: TOCType };
  specialDatasets?: { datasetConfig: Record<string, SpecialDatasetConfig> };
  layerIdToDownloadLayer?: Record<string, string>;
}

// Swap layer config bundles to the DB-derived version. Gating is the server's call: it omits the
// payload when the snapshot read produced nothing, so `layerConfig === undefined` means stay on
// legacy. Each sub-bundle is gated independently on its own presence and resets to legacy (special
// datasets: empty) otherwise, so no stale org leaks on switch. Returns true if any bundle applied.
// Digitization must fill before TOC (singletons spliced).
export function applyDbDrivenLayerConfig(layerConfig: DbLayerConfig | undefined): boolean {
  let applied = false;

  const digitization = layerConfig?.digitization;
  // Require both tree and ids to avoid inconsistent state.
  if (digitization?.digitizationTOC?.length && digitization?.digitizationLayerIds?.length) {
    applyDbDigitizationBundle(digitization);
    applied = true;
  } else {
    resetDigitizationToLegacy();
  }

  const specialDatasets = layerConfig?.specialDatasets;
  // Gate independently; reset must run on every path so a prior org doesn't leak on switch.
  if (specialDatasets?.datasetConfig && Object.keys(specialDatasets.datasetConfig).length > 0) {
    applyDbSpecialDatasetsBundle(specialDatasets);
    applied = true;
  } else {
    clearSpecialDatasets();
  }

  const downloadMap = layerConfig?.layerIdToDownloadLayer;
  // Gate independently; reset must run on every path so a prior org doesn't leak on switch.
  if (downloadMap && Object.keys(downloadMap).length > 0) {
    applyDbDownloadLayersBundle(downloadMap);
    applied = true;
  } else {
    resetDownloadLayersToLegacy();
  }

  if (layerConfig?.toc?.length) {
    applyDbTocBundle({ TOC: layerConfig.toc });
    applied = true;
  } else {
    resetActiveTocToLegacy();
  }

  return applied;
}

// Apply layer config with a hard guarantee never to block org bootstrap. structuredClone
// throws on a valtio Proxy / malformed data; unguarded it aborts setCurrentOrganization, emptying
// projects + hanging /admin. On failure, force all bundles to legacy, re-run TOC setup, and swallow:
// layer config is best-effort, core nav is not. applyToc is a callback to avoid the layers-store
// import cycle; onComplete runs in finally for cleanup on any path.
export function applyLayerConfigWithFallback(
  layerConfig: DbLayerConfig | undefined,
  applyToc: () => void,
  onComplete?: () => void,
): void {
  try {
    applyDbDrivenLayerConfig(layerConfig);
    applyToc();
  } catch (err) {
    console.error(err, 'Layer config setup failed; falling back to legacy TOC (CG-366)');
    try {
      applyDbDrivenLayerConfig(undefined); // undefined → every bundle resets to legacy or empty
      applyToc();
    } catch (inner) {
      console.error(inner, 'Legacy TOC fallback also failed (CG-366)');
    }
  } finally {
    try {
      onComplete?.();
    } catch (completeError) {
      console.error(completeError, 'Layer config completion step failed (CG-366)');
    }
  }
}
